import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "itdukes_tool_session";
const TOOL_ACCESS_ID_KEY = "itdukes_tool_access_id";

interface ToolAccessState {
  isVerified: boolean;
  isLoading: boolean;
  toolAccessId: string | null;
  fullName: string | null;
  email: string | null;
}

export const useToolAccess = () => {
  const [state, setState] = useState<ToolAccessState>({
    isVerified: false,
    isLoading: true,
    toolAccessId: null,
    fullName: null,
    email: null,
  });

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      const sessionToken = sessionStorage.getItem(SESSION_KEY);
      
      if (!sessionToken) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("tool-access", {
          body: { action: "validate-session", sessionToken },
        });

        if (error) throw error;

        if (data?.valid) {
          setState({
            isVerified: true,
            isLoading: false,
            toolAccessId: data.toolAccessId,
            fullName: data.fullName,
            email: data.email,
          });
        } else {
          // Invalid session, clear it
          sessionStorage.removeItem(SESSION_KEY);
          sessionStorage.removeItem(TOOL_ACCESS_ID_KEY);
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error("Error validating session:", error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkSession();
  }, []);

  const requestOTP = useCallback(async (email: string, fullName: string, country: string) => {
    const { data, error } = await supabase.functions.invoke("tool-access", {
      body: { action: "request-otp", email, fullName, country },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return data;
  }, []);

  const verifyOTP = useCallback(async (email: string, otp: string) => {
    const { data, error } = await supabase.functions.invoke("tool-access", {
      body: { action: "verify-otp", email, otp },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    if (data?.success) {
      sessionStorage.setItem(SESSION_KEY, data.sessionToken);
      sessionStorage.setItem(TOOL_ACCESS_ID_KEY, data.toolAccessId);
      setState({
        isVerified: true,
        isLoading: false,
        toolAccessId: data.toolAccessId,
        fullName: data.fullName,
        email: email,
      });
    }

    return data;
  }, []);

  const logToolUsage = useCallback(async (toolId: string, toolName: string, resultSummary?: object) => {
    const toolAccessId = sessionStorage.getItem(TOOL_ACCESS_ID_KEY);
    if (!toolAccessId) return;

    try {
      await supabase.functions.invoke("tool-access", {
        body: { 
          action: "log-usage", 
          toolAccessId, 
          toolId, 
          toolName,
          resultSummary,
        },
      });
    } catch (error) {
      console.error("Error logging tool usage:", error);
    }
  }, []);

  const submitIssue = useCallback(async (
    issueType: string,
    description: string,
    toolId?: string,
    toolName?: string
  ) => {
    const toolAccessId = sessionStorage.getItem(TOOL_ACCESS_ID_KEY);

    const { data, error } = await supabase.functions.invoke("tool-access", {
      body: { 
        action: "submit-issue",
        toolAccessId,
        email: state.email || "anonymous@unknown.com",
        fullName: state.fullName,
        toolId,
        toolName,
        issueType,
        description,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return data;
  }, [state.email, state.fullName]);

  return {
    ...state,
    requestOTP,
    verifyOTP,
    logToolUsage,
    submitIssue,
  };
};